// Spec 078 FR-015/FR-017 + Spec 080 — papel do parâmetro na cena 3D (declarado, nunca deduzido) e a
// regra de alarme por limite (só numérico).
//
// Spec 080: "Parada térmica" mudou de natureza — deixou de espelhar uma temperatura medida e passou
// a espelhar o DISPARO que para a máquina. Vale em booleano e numérico, e só sobre parâmetro
// marcado como restrição operacional (sem a marca ele nunca para a máquina e o alerta nunca
// acenderia). Os limites continuam existindo como regra de alarme, mas não se falam mais com o
// papel. Campos numéricos sem `0` literal como default.
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { ParamForm } from "./types";

interface VisualRoleFieldsProps {
  form: ParamForm;
  setForm: (updater: (f: ParamForm) => ParamForm) => void;
}

export function limitsInvalid(form: ParamForm): string | null {
  if (form.category === "NUMERIC") {
    const max = form.limitMax.trim() === "" ? null : Number(form.limitMax);
    const min = form.limitMin.trim() === "" ? null : Number(form.limitMin);
    if ((max !== null && !Number.isFinite(max)) || (min !== null && !Number.isFinite(min)))
      return "Limite inválido.";
    if (max !== null && min !== null && min >= max)
      return "O limite mínimo deve ser menor que o máximo.";
    // FR-001c — sem faixa não há o que violar; a API recusa com RESTRICTION_REQUIRES_LIMIT.
    if (form.isRestriction && max === null && min === null)
      return "Uma restrição operacional numérica precisa de limite máximo ou mínimo.";
  }
  // FR-004a — o alerta da cena nasce de uma parada; sem a marca de restrição ele nunca acenderia.
  if (form.visualRole === "TEMPERATURE" && !form.isRestriction)
    return "A parada térmica exige o parâmetro marcado como restrição operacional.";
  return null;
}

export function VisualRoleFields({ form, setForm }: VisualRoleFieldsProps) {
  const category = form.category;
  if (category !== "NUMERIC" && category !== "COUNTER" && category !== "BOOLEAN") return null;
  const isNumeric = category === "NUMERIC";
  // Spec 080 — as duas categorias que podem ser restrição e, portanto, derrubar a máquina.
  const isThermalCapable = category === "NUMERIC" || category === "BOOLEAN";
  const error = limitsInvalid(form);
  return (
    <div className="space-y-3 rounded-md border border-border-subtle p-2.5">
      <div className="space-y-1.5">
        <Label htmlFor="p-visual-role">Papel na cena 3D</Label>
        <select
          id="p-visual-role"
          value={form.visualRole}
          onChange={(e) =>
            setForm((f) => ({ ...f, visualRole: e.target.value as ParamForm["visualRole"] }))
          }
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
        >
          <option value="NONE">Nenhum</option>
          {isThermalCapable ? (
            <option value="TEMPERATURE">Parada térmica — acende o alerta na cena</option>
          ) : (
            <option value="FLOW">Vazão — dita a velocidade do material</option>
          )}
        </select>
        <p className="text-[10px] text-muted-foreground">
          {isThermalCapable
            ? "Só uma parada térmica por máquina. Exige a marca de restrição operacional: o alerta acende enquanto a parada causada por este parâmetro estiver em aberto — inclusive fora de produção."
            : "Só um contador de vazão por máquina. Sem papel, o material corre em ritmo único na cena."}
        </p>
      </div>
      {isNumeric ? (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1.5">
            <Label htmlFor="p-limit-max">Limite máximo{form.unit ? ` (${form.unit})` : ""}</Label>
            <Input
              id="p-limit-max"
              type="number"
              inputMode="decimal"
              step="any"
              placeholder="0"
              value={form.limitMax}
              onChange={(e) => setForm((f) => ({ ...f, limitMax: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-limit-min">Limite mínimo</Label>
            <Input
              id="p-limit-min"
              type="number"
              inputMode="decimal"
              step="any"
              placeholder="0"
              value={form.limitMin}
              onChange={(e) => setForm((f) => ({ ...f, limitMin: e.target.value }))}
            />
          </div>
          <p className="col-span-2 text-[10px] text-muted-foreground">
            Fora do intervalo abre um alarme na máquina; voltar ao intervalo fecha sozinho. Vale
            para qualquer numérico — e se o parâmetro for restrição operacional, a mesma faixa
            também para a máquina.
          </p>
        </div>
      ) : null}
      {/* Fora do bloco numérico: a pré-condição do papel vale igual no booleano do relé. */}
      {error ? <p className="text-[11px] text-rd-danger-600">{error}</p> : null}
    </div>
  );
}
